import type { TaskTimelineEntry } from '@erp/rpc';
import { lazy, Suspense, useState, type ReactElement } from 'react';
import { T, useGT } from 'gt-react';
import { ListBulletsIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { Skeleton } from 'src/components/ui/skeleton';

const TaskRunInspector = lazy(() =>
  import('./TaskRunInspector').then((module) => ({
    default: module.TaskRunInspector,
  })),
);

/** Keep the trigger mounted so closing inspection restores focus and leaves the
 * timeline's scroll position intact. Load the trace only when requested. */
export function TaskRunEvidenceButton({
  agentRunId,
  trigger,
  eventDetails,
  runDetails,
  stepCount,
  agentName,
  operatorStepId,
  allowed,
  summary,
  outcome,
}: {
  agentRunId: string;
  trigger?: ReactElement;
  eventDetails?: Extract<TaskTimelineEntry, { kind: 'operator_step' }>;
  stepCount?: number;
  runDetails?: Extract<TaskTimelineEntry, { kind: 'agent_run' }>;
  agentName?: string | null;
  operatorStepId?: string;
  allowed: boolean;
  summary?: string | null;
  outcome?: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (!allowed)
    return (
      <p className="text-xs text-muted-foreground">
        <T context="task run inspection permission">
          Agent access is required to inspect run details.
        </T>
      </p>
    );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <ListBulletsIcon data-icon="inline-start" />
            <ViewStepsLabel count={stepCount} />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex h-[90vh] w-[96vw] max-w-none flex-col overflow-hidden p-3 sm:max-w-none">
        <DialogHeader>
          <DialogTitle>
            {agentName ?? (
              <T context="task run inspector title">Agent Run Details</T>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {runDetails && (
            <details className="mb-2 w-full text-xs text-muted-foreground">
              <summary className="cursor-pointer">
                <T context="task run original lifecycle and provenance disclosure">
                  Run Details
                </T>
              </summary>
              <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all">
                {JSON.stringify(runDetails, null, 2)}
              </pre>
            </details>
          )}
          {eventDetails && (
            <details className="mb-2 w-full text-xs text-muted-foreground">
              <summary className="cursor-pointer">
                <T context="task timeline original operator event disclosure">
                  Event Details
                </T>
              </summary>
              <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all">
                {JSON.stringify(eventDetails, null, 2)}
              </pre>
            </details>
          )}
          {open && (
            <Suspense fallback={<Skeleton className="h-64 w-full" />}>
              <TaskRunInspector
                key={agentRunId}
                agentRunId={agentRunId}
                initialOperatorStepId={operatorStepId}
                summary={summary}
                outcome={outcome}
              />
            </Suspense>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewStepsLabel({ count: stepCount }: { count?: number }) {
  const gt = useGT();
  return (
    <>
      {stepCount === 1
        ? gt('View 1 Step', {
            context: 'task timeline button to inspect one recorded step',
          })
        : stepCount === undefined
          ? gt('View Steps', {
              context: 'task timeline button to inspect agent evidence',
            })
          : gt('View {count} Steps', {
              context:
                'task timeline button to inspect a recorded number of steps',
              count: String(stepCount),
            })}
    </>
  );
}
